import type { ReactNode } from 'react';
import { Navigate } from 'react-router';

import { Loader } from '@/components/ui/loader';

import type { FeatureFlagKey } from '../feature-flag.constants';
import { useFeature } from '../feature-flag.hooks';

type FeatureRouteProps = {
  flag: FeatureFlagKey;
  children: ReactNode;
  /** Where a client without the feature is sent. The planner by default. */
  redirectTo?: string;
};

/**
 * Guards a whole page by release flag.
 *
 * A component rather than a route loader on purpose: `CmsLayout` never
 * revalidates its loaders, so a loader would not re-run when a super Admin
 * switches to a tenant that has the feature off. This re-renders instead.
 *
 * @example
 * { path: '/bookings/v2', element: (
 *   <FeatureRoute flag={FeatureFlagKey.BOOKING_MODULAR_FLOW}>
 *     <BookingV2Page />
 *   </FeatureRoute>
 * ) }
 */
const FeatureRoute = ({
  flag,
  children,
  redirectTo = '/planner',
}: FeatureRouteProps) => {
  const { enabled, isLoading } = useFeature(flag);

  if (isLoading) {
    return (
      <div className="flex w-full justify-center py-16">
        <Loader className="size-6" />
      </div>
    );
  }

  if (!enabled) return <Navigate to={redirectTo} replace />;

  return <>{children}</>;
};

export { FeatureRoute };
